import { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Search } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandItem } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

interface HashtagSearchProps {
 value: string;
 onChange: (value: string) => void;
 allHashtags: string[];
}

export function HashtagSearch({ value, onChange, allHashtags }: HashtagSearchProps) {
 const [open, setOpen] = useState(false);
 const [inputValue, setInputValue] = useState(value);
 const inputRef = useRef<HTMLInputElement>(null);

 useEffect(() => {
   setInputValue(value);
 }, [value]);

 // Add this new useEffect for auto-focus
 useEffect(() => {
   if (inputRef.current) {
     inputRef.current.focus();
     // If there's a value, put cursor at the end
     if (inputValue) {
       const length = inputValue.length;
       inputRef.current.setSelectionRange(length, length);
     }
   }
 }, []); // Empty dependency array means this runs once when component mounts

 const handleInputChange = (newValue: string) => {
   setInputValue(newValue);
   onChange(newValue);

   const lastHashIndex = newValue.lastIndexOf('#');
   if (lastHashIndex !== -1 && lastHashIndex < newValue.length - 1) {
     const hashtagPart = newValue.slice(lastHashIndex + 1).toLowerCase();
     if (hashtagPart.length > 0) {
       setOpen(true);
     } else {
       setOpen(false);
     }
   } else {
     setOpen(false);
   }
 };

 const handleSelectHashtag = (tag: string) => {
   const lastHashIndex = inputValue.lastIndexOf('#');
   if (lastHashIndex !== -1) {
     const newValue = inputValue.slice(0, lastHashIndex) + `#${tag}`;
     setInputValue(newValue);
     onChange(newValue);
     
     // Use setTimeout to ensure the focus is set after the Popover closes
     setTimeout(() => {
       if (inputRef.current) {
         inputRef.current.focus();
         // Place cursor at the end of the input
         const length = newValue.length;
         inputRef.current.setSelectionRange(length, length);
       }
     }, 0);
   }
   setOpen(false);
 };

 // Filter tags based on the text after the last # character
 const filteredTags = allHashtags.filter(tag => {
   const lastHashIndex = inputValue.lastIndexOf('#');
   if (lastHashIndex === -1) return false;

   const searchText = inputValue.slice(lastHashIndex + 1).toLowerCase().trim();
   return searchText.length > 0 && tag.toLowerCase().includes(searchText);
 });

 return (
   <div className="relative w-full">
     <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
     <Popover open={open && filteredTags.length > 0} onOpenChange={setOpen}>
       <PopoverTrigger asChild>
         <div className="w-full">
           <Input
             ref={inputRef}
             placeholder="Search actions..."
             className="pl-8 w-full"
             value={inputValue}
             onChange={(e) => handleInputChange(e.target.value)}
             onKeyDown={(e) => {
               if (e.key === 'Escape') {
                 setOpen(false);
               } else if (e.key === 'Enter' && filteredTags.length > 0) {
                 e.preventDefault();
                 handleSelectHashtag(filteredTags[0]);
               } else if (e.key === 'ArrowDown' && filteredTags.length > 0) {
                 e.preventDefault();
                 // Keep focus on input but allow navigation in suggestions
               }
             }}
           />
         </div>
       </PopoverTrigger>
       <PopoverContent
         className="w-[400px] p-0"
         align="start"
         sideOffset={5}
         onOpenAutoFocus={(e) => {
           // Prevent the popover from stealing focus
           e.preventDefault();
         }}
       >
         <Command>
           <CommandEmpty>No hashtags found.</CommandEmpty>
           <CommandGroup>
             {filteredTags.map((tag) => (
               <CommandItem
                 key={tag}
                 value={tag}
                 onSelect={() => handleSelectHashtag(tag)}
                 className="cursor-pointer"
               >
                 #{tag}
               </CommandItem>
             ))}
           </CommandGroup>
         </Command>
       </PopoverContent>
     </Popover>
   </div>
 );
}